/*
  The table header (§6.2, §6.4). Replaces MUI's TableSortLabel.

  aria-sort sits on the <th>: "ascending"/"descending" on the active column and "none" on
  every other sortable one, so assistive technology hears which column is active and not
  merely that the table is sortable. Non-sortable columns carry no aria-sort at all.

  The direction indicator is an icon plus an sr-only phrase. The icon alone is decorative;
  the phrase is what a screen reader gets on the button itself, which is what the head
  this replaces did and is worth keeping.
*/

import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";

import { FOCUS_RING } from "components/styles";

import { ariaSortFor, columnAlignClass, isColumnSortable, nextSortState } from "./columns";
import { TABLE_CELL_PADDING_TIGHT, TABLE_HEAD_CELL } from "./tableStyles";
import { DataTableColumn, TableSortState } from "./types";

export interface DataTableHeadProps<TRow> {
  columns: readonly DataTableColumn<TRow>[];
  sort: TableSortState;
  /* Omit to render a header with no sort affordance at all — D19's SearchCardsRowTable. */
  onSortChange?: (sort: TableSortState) => void;
}

const ICON_CLASS = "h-3.5 w-3.5 shrink-0";

function DataTableHead<TRow>(props: DataTableHeadProps<TRow>) {
  const { columns, sort, onSortChange } = props;

  return (
    <thead>
      <tr>
        {columns.map((column) => {
          const sortable = isColumnSortable(column) && !!onSortChange;
          const active = sortable && sort.orderBy === column.id;
          const alignClass = columnAlignClass(column);
          const labelClass = column.labelHidden ? "sr-only" : undefined;
          /*
            A short header paints the short text and keeps the full label for assistive
            technology and as the tooltip, so "Flawless 10" still announces as
            "Flawless 10 (Quad)".
          */
          const labelNode = column.shortLabel && !column.labelHidden ? (
            <>
              <span aria-hidden="true">{column.shortLabel}</span>
              <span className="sr-only">{column.label}</span>
            </>
          ) : (
            <span className={labelClass}>{column.label}</span>
          );

          return (
            <th
              key={column.id}
              scope="col"
              aria-sort={sortable ? ariaSortFor(column, sort) : undefined}
              title={column.shortLabel ? column.label : undefined}
              className={`${TABLE_HEAD_CELL} ${alignClass} ${
                column.compact ? TABLE_CELL_PADDING_TIGHT : ""
              } ${column.className ?? ""}`}
            >
              {sortable && onSortChange ? (
                <button
                  type="button"
                  onClick={() => onSortChange(nextSortState(columns, sort, column.id))}
                  className={`inline-flex items-center rounded ${
                    column.compact ? "flex-col gap-0.5" : "gap-1"
                  } ${FOCUS_RING} ${
                    active ? "text-pcg-goldText" : "hover:text-pcg-text"
                  }`}
                >
                  {labelNode}
                  {active ? (
                    sort.order === "asc" ? (
                      <ChevronUp aria-hidden="true" className={ICON_CLASS} />
                    ) : (
                      <ChevronDown aria-hidden="true" className={ICON_CLASS} />
                    )
                  ) : (
                    <ChevronsUpDown
                      aria-hidden="true"
                      className={`${ICON_CLASS} opacity-50`}
                    />
                  )}
                  <span className="sr-only">
                    {active
                      ? sort.order === "asc"
                        ? ", sorted ascending"
                        : ", sorted descending"
                      : ", not sorted"}
                  </span>
                </button>
              ) : (
                labelNode
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

export default DataTableHead;
